/** AES-256-GCM share payloads with PBKDF2-derived keys; base64url for URL safety. */

export const SHARE_QUERY_PARAM = 's';
/** Passphrase in the URL; used to decrypt {@link SHARE_QUERY_PARAM}. */
export const SHARE_KEY_PARAM = 'k';
/** Max length of encrypted `s` param (base64url). */
export const MAX_SHARE_PARAM_CHARS = 15_500;
/** Max total URL length including `s` and `k` (browser / server limits). */
export const MAX_SHARE_URL_CHARS = 16_000;
export const MAX_SHARE_KEY_PARAM_CHARS = 256;

const FORMAT_VERSION = 1;
const SALT_BYTES = 16;
const IV_BYTES = 12;
const PBKDF2_ITERATIONS = 310_000;

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlToBytes(encoded: string): Uint8Array {
  const trimmed = encoded.trim();
  if (!trimmed || !/^[A-Za-z0-9_-]+$/.test(trimmed)) {
    throw new Error('Invalid share link encoding (expected base64url).');
  }
  let b64 = trimmed.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4;
  if (pad === 2) b64 += '==';
  else if (pad === 3) b64 += '=';
  else if (pad === 1) throw new Error('Invalid share link encoding (bad length).');
  let binary: string;
  try {
    binary = atob(b64);
  } catch {
    throw new Error('Invalid share link encoding (not valid base64).');
  }
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

async function deriveAesKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const saltBuf = Uint8Array.from(salt);
  const baseKey = await crypto.subtle.importKey('raw', enc.encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: saltBuf, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/** Encrypt UTF-8 JSON/text; returns base64url bundle (version + salt + iv + ciphertext). */
export async function encryptShareBlob(plaintext: string, passphrase: string): Promise<string> {
  const pass = passphrase.trim();
  if (!pass) throw new Error('Passphrase cannot be empty.');
  const enc = new TextEncoder();
  const data = enc.encode(plaintext);
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const key = await deriveAesKey(pass, salt);
  const cipherBuf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);
  const cipher = new Uint8Array(cipherBuf);
  const packed = new Uint8Array(1 + SALT_BYTES + IV_BYTES + cipher.length);
  packed[0] = FORMAT_VERSION;
  packed.set(salt, 1);
  packed.set(iv, 1 + SALT_BYTES);
  packed.set(cipher, 1 + SALT_BYTES + IV_BYTES);
  const encoded = bytesToBase64Url(packed);
  if (encoded.length > MAX_SHARE_PARAM_CHARS) {
    throw new Error(
      `LINK_TOO_LONG:${encoded.length}`
    );
  }
  return encoded;
}

/** Decrypt a base64url bundle from {@link encryptShareBlob}. */
export async function decryptShareBlob(encoded: string, passphrase: string): Promise<string> {
  const pass = passphrase.trim();
  if (!pass) throw new Error('Passphrase cannot be empty.');
  const raw = base64UrlToBytes(encoded);
  if (raw.length < 1 + SALT_BYTES + IV_BYTES + 16 || raw[0] !== FORMAT_VERSION) {
    throw new Error('Unrecognized or corrupted share link.');
  }
  const salt = raw.slice(1, 1 + SALT_BYTES);
  const iv = raw.slice(1 + SALT_BYTES, 1 + SALT_BYTES + IV_BYTES);
  const cipher = new Uint8Array(raw.slice(1 + SALT_BYTES + IV_BYTES));
  const ivBuf = new Uint8Array(iv);
  const key = await deriveAesKey(pass, salt);
  try {
    const plainBuf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: ivBuf }, key, cipher);
    return new TextDecoder().decode(plainBuf);
  } catch {
    throw new Error('Could not decrypt. Check the passphrase and link.');
  }
}

export type ShareLinkFromLocation = {
  payload: string;
  key: string | null;
};

export function readShareLinkFromLocation(): ShareLinkFromLocation | null {
  return parseShareLinkFromHref(window.location.href);
}

/** Parse `?s=` / optional `?k=` from a full or relative share URL. */
export function parseShareLinkFromHref(href: string): ShareLinkFromLocation | null {
  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return null;
  }
  const raw = url.searchParams.get(SHARE_QUERY_PARAM);
  if (!raw) return null;
  return { payload: raw, key: url.searchParams.get(SHARE_KEY_PARAM) };
}

/** @deprecated Use {@link readShareLinkFromLocation}. */
export function readShareParamFromLocation(): string | null {
  return readShareLinkFromLocation()?.payload ?? null;
}

function assertShareUrlLength(url: URL): void {
  const href = url.href;
  if (href.length > MAX_SHARE_URL_CHARS) {
    throw new Error(`LINK_URL_TOO_LONG:${href.length}`);
  }
  const key = url.searchParams.get(SHARE_KEY_PARAM) ?? '';
  if (key.length > MAX_SHARE_KEY_PARAM_CHARS) {
    throw new Error('Passphrase is too long for a share link.');
  }
}

export type ShareLinkLocation = Pick<Location, 'protocol' | 'hostname' | 'host' | 'pathname'>;

/** Production share links always use HTTPS (localhost keeps current protocol for dev). */
export function shareLinkOrigin(loc: ShareLinkLocation = window.location): string {
  const { protocol, hostname, host } = loc;
  const local =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname.endsWith('.localhost');
  if (local) return `${protocol}//${host}`;
  return `https://${host}`;
}

export type BuildShareUrlOptions = {
  /** When true, append `?k=` so recipients decrypt without typing the passphrase. */
  includeKeyInUrl: boolean;
};

/** Build share URL: `?s=` ciphertext; optional `?k=` passphrase. */
export function buildShareDecryptUrl(
  encodedPayload: string,
  passphrase: string,
  options: BuildShareUrlOptions
): string {
  const pass = passphrase.trim();
  if (!pass) throw new Error('Passphrase cannot be empty.');
  if (encodedPayload.length > MAX_SHARE_PARAM_CHARS) {
    throw new Error(`Encrypted payload exceeds ${MAX_SHARE_PARAM_CHARS} characters.`);
  }
  if (options.includeKeyInUrl && pass.length > MAX_SHARE_KEY_PARAM_CHARS) {
    throw new Error(`Passphrase exceeds ${MAX_SHARE_KEY_PARAM_CHARS} characters.`);
  }
  const loc = window.location;
  // Always use site root so opening the link loads the app entry and ShareDecryptView (?s=).
  const url = new URL('/', `${shareLinkOrigin(loc)}/`);
  url.search = '';
  url.searchParams.set(SHARE_QUERY_PARAM, encodedPayload);
  if (options.includeKeyInUrl) {
    url.searchParams.set(SHARE_KEY_PARAM, pass);
  }
  assertShareUrlLength(url);
  return url.toString();
}
